import { useState } from "react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import WhatsAppShareButton from "./WhatsAppShareButton";
import {
  MessageCircle,
  MapPin,
  Mail,
  Phone,
  Instagram,
  ArrowUpRight,
  Send,
} from "lucide-react";

const INSTAGRAM_HANDLES = ["instituto.ga", "enaga.ga", "futebolclubepe", "missaomaiss"];

export default function Contact() {
  const cfg = useConfig();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Preencha nome, e-mail e mensagem.");
      return;
    }
    setSubmitting(true);
    try {
      await endpoints.contact(form);
      toast.success("Mensagem enviada! Retornaremos em breve.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Não foi possível enviar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contato"
      data-testid="contact-section"
      className="section-pad bg-ga-ink text-white relative overflow-hidden"
    >
      <div className="absolute top-20 right-20 w-80 h-80 rounded-full bg-ga-emerald/20 blur-3xl" />

      <div className="container-ga relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="pill border-white/20 text-white/80 bg-white/5 mb-6" data-testid="contact-pill">
              Contato
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Vamos <span className="text-ga-amber">conversar</span>.
            </h2>
            <p className="mt-6 text-white/70 text-lg max-w-md">
              Parcerias, doações, imprensa ou agendamento. Fale com a gente. Respondemos de segunda a sexta em até 24 horas.
            </p>

            <div className="mt-6" data-testid="contact-share-wrapper">
              <WhatsAppShareButton label="Compartilhar o Instituto no WhatsApp" />
            </div>

            <div className="mt-10 space-y-4">
              {cfg.whatsapp_url && (
                <a
                  href={cfg.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="group flex items-center gap-4 rounded-3xl border border-white/10 p-5 hover:border-ga-emerald hover:bg-ga-emerald/5 transition-all"
                >
                  <div className="w-12 h-12 rounded-2xl bg-ga-emerald flex items-center justify-center">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold">WhatsApp</div>
                    <div className="font-bold">Falar com o Instituto</div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 opacity-60 group-hover:opacity-100 group-hover:-translate-y-1 transition-all" />
                </a>
              )}

              {cfg.endereco && (
                <div className="flex items-center gap-4 rounded-3xl border border-white/10 p-5">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold">Sede</div>
                    <div className="font-bold">{cfg.endereco}</div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {cfg.contact_email && (
                  <a
                    href={`mailto:${cfg.contact_email}`}
                    data-testid="contact-email"
                    className="flex items-center gap-3 rounded-3xl border border-white/10 p-4 hover:border-white/30 transition-colors"
                  >
                    <Mail className="w-4 h-4 shrink-0" />
                    <span className="text-sm font-semibold truncate">{cfg.contact_email}</span>
                  </a>
                )}
                {cfg.telefone && (
                  <a
                    href={cfg.whatsapp_url || `tel:+55${cfg.telefone.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-phone"
                    className="flex items-center gap-3 rounded-3xl border border-white/10 p-4 hover:border-white/30 transition-colors"
                  >
                    <Phone className="w-4 h-4 shrink-0" />
                    <span className="text-sm font-semibold">{cfg.telefone}</span>
                  </a>
                )}
              </div>

              <div className="flex items-center gap-2 pt-2">
                {INSTAGRAM_HANDLES.map((h) => (
                  <a
                    key={h}
                    href={`https://www.instagram.com/${h}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${h}`}
                    className="w-10 h-10 rounded-full bg-white/5 hover:bg-white hover:text-ga-ink flex items-center justify-center transition-colors"
                    aria-label={h}
                  >
                    <Instagram className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-[2rem] bg-white text-ga-ink p-6 sm:p-8 border border-white/10 shadow-2xl"
            >
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-ga-emerald">
                Envie uma mensagem
              </div>
              <h3 className="font-display font-bold text-2xl mt-2 mb-6">
                Conte como podemos caminhar juntos.
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  required
                  placeholder="Seu nome"
                  value={form.name}
                  onChange={onChange("name")}
                  data-testid="contact-input-name"
                  className="rounded-xl border border-border px-4 py-3 bg-white focus:outline-none focus:border-ga-emerald"
                />
                <input
                  type="email"
                  required
                  placeholder="E-mail"
                  value={form.email}
                  onChange={onChange("email")}
                  data-testid="contact-input-email"
                  className="rounded-xl border border-border px-4 py-3 bg-white focus:outline-none focus:border-ga-emerald"
                />
                <input
                  type="tel"
                  placeholder="Telefone"
                  value={form.phone}
                  onChange={onChange("phone")}
                  data-testid="contact-input-phone"
                  className="rounded-xl border border-border px-4 py-3 bg-white focus:outline-none focus:border-ga-emerald"
                />
                <select
                  value={form.subject}
                  onChange={onChange("subject")}
                  data-testid="contact-input-subject"
                  className="rounded-xl border border-border px-4 py-3 bg-white focus:outline-none focus:border-ga-emerald"
                >
                  <option value="">Assunto</option>
                  <option value="doacao">Doação e parceria</option>
                  <option value="enaga">Agendamento ENAGA</option>
                  <option value="oracao">Pedido de oração</option>
                  <option value="imprensa">Imprensa</option>
                  <option value="outro">Outro</option>
                </select>
                <textarea
                  required
                  rows={5}
                  placeholder="Sua mensagem"
                  value={form.message}
                  onChange={onChange("message")}
                  data-testid="contact-input-message"
                  className="sm:col-span-2 rounded-xl border border-border px-4 py-3 bg-white focus:outline-none focus:border-ga-emerald resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-5 w-full btn-primary disabled:opacity-60"
              >
                {submitting ? "Enviando..." : (<><Send className="w-4 h-4" /> Enviar mensagem</>)}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-20 pt-10 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-sm text-white/50">
          <div>
            © {new Date().getFullYear()} Instituto GA+. Todos os direitos
            reservados.
          </div>
          <div className="flex flex-wrap gap-5">
            <a href="#doar" className="hover:text-white transition-colors">Doar</a>
            <a href="#pilar-saude" className="hover:text-white transition-colors">Saúde ENAGA</a>
            <a href="#pilar-cristao" className="hover:text-white transition-colors">Projetos</a>
            <a href="#contato" className="hover:text-white transition-colors">Contato</a>
          </div>
        </div>
      </div>
    </section>
  );
}
